import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Globe, Mail, MapPin, MessageSquare, Phone, Send } from 'lucide-react';

const CONTACT_SMS_NUMBERS = ['+233200738204', '+233256895006'];

const sendSMS = async (phoneNumber, message) => {
  const hubtelEndpoint = 'https://smsc.hubtel.com/v1/messages/send';
  const clientId = process.env.REACT_APP_HUBTEL_CLIENT_ID || 'vxojxzbs';
  const clientSecret = process.env.REACT_APP_HUBTEL_CLIENT_SECRET || 'uznaitfd';
  try {
    const credentials = btoa(`${clientId}:${clientSecret}`);
    const response = await fetch(hubtelEndpoint, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${credentials}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        From: 'AFB',
        To: phoneNumber.replace(/\s/g, ''),
        Content: message
      })
    });
    return response.ok;
  } catch (err) {
    console.error('SMS sending error:', err);
    return false;
  }
};

const Contact = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus('');
    const smsContent = `AFB Contact: ${formData.firstName} ${formData.lastName}. Email: ${formData.email}. Service: ${formData.service}. Message: ${formData.message}`;
    try {
      const results = await Promise.all(CONTACT_SMS_NUMBERS.map((number) => sendSMS(number, smsContent)));
      if (results.every(Boolean)) {
        setStatus('Thank you. We will contact you soon.');
        setFormData({ firstName: '', lastName: '', email: '', company: '', phone: '', service: '', message: '' });
      } else {
        setStatus('Your message was received, and we will still follow up directly.');
      }
    } catch (error) {
      setStatus('Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: 'Email', details: ['financier4@yahoo.com', 'franciskontoh4@gmail.com'] },
    { icon: Phone, title: 'Phone', details: ['+233 200738204', '+233 256895006'] },
    { icon: MapPin, title: 'Location', details: ['PEDUASE, Eastern Region'] },
    { icon: Clock, title: 'Hours', details: ['Mon-Fri: 9:00 AM - 6:00 PM'] }
  ];

  return (
    <div className="min-h-screen bg-[#f8fbff] pt-24">
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Contact</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-900 sm:text-5xl">We are ready to help you move forward.</h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">Share your goals and we will respond with a thoughtful next step.</p>
          </motion.div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {contactInfo.map((item, index) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <item.icon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">{item.title}</h2>
                {item.details.map((detail) => (
                  <p key={detail} className="mt-2 text-sm leading-7 text-slate-600">{detail}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Location</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">A local presence with a global outlook.</h2>
            <div className="mt-8 space-y-4">
              <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
                    <Globe className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">African Financial Bridge</h3>
                    <p className="mt-2 text-base leading-7 text-slate-600">We support clients and communities through practical finance, trusted advisory, and sustainable partnerships.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Send a message</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">Let us know how we can help.</h2>
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <input name="firstName" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="First name" value={formData.firstName} onChange={handleChange} required />
                <input name="lastName" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Last name" value={formData.lastName} onChange={handleChange} required />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <input type="email" name="email" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Email address" value={formData.email} onChange={handleChange} required />
                <input name="phone" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Phone number" value={formData.phone} onChange={handleChange} />
              </div>
              <input name="company" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Company" value={formData.company} onChange={handleChange} />
              <input name="service" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Service interest" value={formData.service} onChange={handleChange} required />
              <textarea rows="4" name="message" className="w-full rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 outline-none" placeholder="Tell us more about your needs" value={formData.message} onChange={handleChange} required />
              <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70">
                <Send className="h-4 w-4" />
                {isSubmitting ? 'Sending…' : 'Send message'}
              </button>
              {status ? <p className="text-sm text-slate-600">{status}</p> : null}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
